/**
 * Compute how many digits the frame component needs for a given fps.
 * Uses Math.ceil so fractional rates like 99.7 → 100 → 3 digits.
 * Always at least 2 digits.
 */
export function frameDigitCount(fps: number): number {
  return Math.max(2, String(Math.ceil(fps)).length);
}

export function formatTimecode(
  seconds: number,
  fps: number = 24,
  startFrame: number = 0,
  dropFrame: boolean = false,
): string {
  const frameDigs = frameDigitCount(fps);
  if (!isFinite(seconds) || seconds < 0) return `00:00:00:${'0'.repeat(frameDigs)}`;

  // Math.round (not floor) — floor causes frame/fps*fps to lose a frame
  // due to IEEE 754 precision (e.g. floor(80.9999999999) = 80 instead of
  // 81). Round correctly recovers the frame index in both frame stepping
  // (where currentTime = targetFrame/fps) and playback (where currentTime
  // is very close to a frame boundary).
  const totalFrames = Math.round(seconds * fps) + startFrame;

  if (dropFrame && (fps === 29.97 || fps === 59.94)) {
    return formatDropFrame(totalFrames, fps, frameDigs);
  }

  const roundFps = Math.round(fps);
  const ff = totalFrames % roundFps;
  const totalSecs = Math.floor(totalFrames / roundFps);
  const ss = totalSecs % 60;
  const mm = Math.floor(totalSecs / 60) % 60;
  const hh = Math.floor(totalSecs / 3600);

  return `${pad(hh)}:${pad(mm)}:${pad(ss)}:${padFrames(ff, frameDigs)}`;
}

/**
 * Convert an actual frame count to a drop-frame timecode string.
 *
 * Drop-frame timecode skips frame numbers :00 and :01 at every minute
 * boundary EXCEPT every 10th minute, keeping wall-clock time aligned
 * with the 29.97/59.94 fps rate.
 *
 * The algorithm:
 * 1. Divide frame count into 10-minute blocks (17982 actual frames each for 29.97)
 * 2. Within each block, count how many minute boundaries have been crossed
 * 3. Each non-10th-minute boundary adds `dropFrames` (2 for 29.97) to the display count
 * 4. Convert the adjusted display frame count to HH:MM:SS;FF
 */
function formatDropFrame(totalFrames: number, fps: number, frameDigs: number): string {
  const dropFrames = fps === 29.97 ? 2 : 4;
  const roundFps = Math.round(fps); // 30 or 60
  // Actual frames per minute at the true rate (e.g. 29.97 * 60 = 1798.2 → 1798)
  const actualFramesPerMin = Math.round(fps * 60);
  // Actual frames per 10-minute block
  const framesPer10Min = Math.round(fps * 600);

  const d = Math.floor(totalFrames / framesPer10Min);
  const m = totalFrames % framesPer10Min;

  // Total drops: 9 drops per 10-min block (minutes 1-9) × dropFrames each,
  // plus within the current block, one drop per minute boundary crossed.
  // Minute boundaries within a block occur every `actualFramesPerMin` frames.
  let adjustedFrames: number;
  if (m < dropFrames) {
    // First `dropFrames` frames of a 10-min block — no within-block drops
    adjustedFrames = totalFrames + dropFrames * 9 * d;
  } else {
    adjustedFrames = totalFrames
      + dropFrames * 9 * d
      + dropFrames * Math.floor((m - dropFrames) / (actualFramesPerMin - dropFrames));
  }

  const ff = adjustedFrames % roundFps;
  const totalSecs = Math.floor(adjustedFrames / roundFps);
  const ss = totalSecs % 60;
  const mm = Math.floor(totalSecs / 60) % 60;
  const hh = Math.floor(totalSecs / 3600);

  return `${pad(hh)}:${pad(mm)}:${pad(ss)};${padFrames(ff, frameDigs)}`;
}

function pad(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

function padFrames(n: number, digits: number): string {
  return String(n).padStart(digits, '0');
}

export function formatTimeOnly(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) return '00:00:00';

  const totalSecs = Math.floor(seconds);
  const ss = totalSecs % 60;
  const mm = Math.floor(totalSecs / 60) % 60;
  const hh = Math.floor(totalSecs / 3600);

  return `${pad(hh)}:${pad(mm)}:${pad(ss)}`;
}

/**
 * Convert a timecode string (HH:MM:SS:FF or HH:MM:SS;FF) back to seconds.
 */
export function timecodeToSeconds(
  timecode: string,
  fps: number,
  startFrame: number = 0,
  dropFrame: boolean = false,
): number {
  const frames = timecodeToFrameCount(timecode, fps, dropFrame);
  return Math.max(0, (frames - startFrame) / fps);
}

/**
 * Convert a timecode string to an actual (absolute) frame count.
 * For drop-frame timecodes, this accounts for the skipped display
 * frame numbers and returns the real frame number.
 */
export function timecodeToFrameCount(
  timecode: string,
  fps: number,
  dropFrame: boolean = false,
): number {
  const parts = timecode.split(/[:;]/);
  if (parts.length < 4) return 0;

  const h = parseInt(parts[0], 10) || 0;
  const m = parseInt(parts[1], 10) || 0;
  const s = parseInt(parts[2], 10) || 0;
  const f = parseInt(parts[3], 10) || 0;

  const roundFps = Math.round(fps);
  let totalFrames: number;

  if (dropFrame && (fps === 29.97 || fps === 59.94)) {
    const dropCount = fps === 29.97 ? 2 : 4;
    // Use total minutes (hours × 60 + minutes) for the drop calculation.
    // The old code used just `m` which was wrong for timecodes with hours > 0.
    const totalMinutes = h * 60 + m;
    totalFrames = (h * 3600 + m * 60 + s) * roundFps + f;
    totalFrames -= dropCount * (totalMinutes - Math.floor(totalMinutes / 10));
  } else {
    totalFrames = (h * 3600 + m * 60 + s) * roundFps + f;
  }

  return totalFrames;
}

export function ariaTimeText(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) return '0 seconds';

  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);

  const parts: string[] = [];
  if (h > 0) parts.push(`${h} hour${h !== 1 ? 's' : ''}`);
  if (m > 0) parts.push(`${m} minute${m !== 1 ? 's' : ''}`);
  parts.push(`${s} second${s !== 1 ? 's' : ''}`);

  return parts.join(' ');
}
